import { useMutation, useQueryClient } from '@tanstack/react-query'
import { mutateComments, DEFAULT_META } from '../data/metaApi.js'
import { useHistoryStore } from '../store/historyStore.js'
import { withArchiveGuard } from './_archiveGuard.js'

/**
 * useCommentsOp — mutation hook for single comment operations.
 *
 * Same safety shape as useActionItemOp — posts one op (create thread,
 * add reply, set resolved, delete thread) to /meta/comments and
 * receives the authoritative merged list back. Meta cache is updated
 * with only the new comments slice; every other field stays untouched.
 *
 * Caller provides an `optimistic` function that mirrors the op on the
 * local cache for instant UI feedback:
 *
 *   op.mutate({
 *     op: 'addReply',
 *     threadId: 't-1',
 *     reply: { id, author, text, at },
 *     optimistic: (threads) =>
 *       threads.map(t => t.id === 't-1'
 *         ? { ...t, replies: [...t.replies, reply] }
 *         : t),
 *   })
 *
 * Archive-view guarded so a user browsing a past week can't mutate
 * live comments through a stale UI click.
 */
export function useCommentsOp() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (payload) => {
      const comments = await mutateComments(payload)
      return { comments }
    },

    onMutate: async ({ optimistic, op }) => {
      await queryClient.cancelQueries({ queryKey: ['meta'] })
      const previous = queryClient.getQueryData(['meta']) || DEFAULT_META

      useHistoryStore
        .getState()
        .pushSnapshot(
          `Comments ${op}`,
          queryClient.getQueryData(['goals']),
          previous
        )

      if (typeof optimistic === 'function') {
        const current = Array.isArray(previous.comments) ? previous.comments : []
        const next = optimistic(current)
        queryClient.setQueryData(['meta'], {
          ...previous,
          comments: next,
        })
      }

      return { previous }
    },

    onSuccess: ({ comments }) => {
      const current = queryClient.getQueryData(['meta']) || DEFAULT_META
      queryClient.setQueryData(['meta'], {
        ...current,
        comments,
      })
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['meta'], ctx.previous)
    },
  })

  return withArchiveGuard(mutation)
}
